// /* eslint-disable */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { UpdateUserServer } from "../../services/apiUtils";
import { notification } from "antd";

// import ApiService from "../../services/httpService";

/**
 * UserDetailEdit is a React component that provides an interface for editing user details.
 * It manages form state and validation for user fields such as Name, City, Address, State, Phone, and Email.
 *
 * Props:
 * - editUser: An object containing the current user details to be edited.
 * - setEditModal: Function to toggle the visibility of the edit modal.
 * - setEditUser: Function to update the user details in the state.
 * - setHideContent: Function to control content visibility.
 *
 * State:
 * - errors: An object for tracking validation errors for form inputs.
 *
 * Functions:
 * - validate: Validates form inputs and updates the errors state.
 * - handleChange: Handles changes to form inputs and triggers validation.
 * - handleBlur: Validates form inputs when focus is lost.
 * - handleSubmit: Handles form submission, validates inputs, and updates the user data on the server.
 *
 * Returns:
 * - A JSX element representing the user edit form.
 */
const UserDetailEdit = ({
  editUser,
  setEditModal,
  setEditUser,
  setHideContent,
}) => {
  const [errors, setErrors] = useState({});

  // Validate form fields
  const validate = () => {
    const newErrors = {};

    if (!editUser.Name) newErrors.Name = "Name is required";
    if (!editUser.City) newErrors.City = "City is required"; // Fixed 'city' key mismatch
    if (!editUser.Address) newErrors.Address = "Address is required";
    if (!editUser.State) newErrors.State = "State is required";

    if (!editUser.Phone) {
      newErrors.Phone = "Phone is required";
    } else if (!/^\d{10}$/.test(editUser.Phone)) {
      newErrors.Phone = "Phone number must be 10 digits";
    }

    return newErrors;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    setEditUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle blur validation
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const newErrors = { ...errors };

    if (name === "Phone") {
      if (!/^\d{10}$/.test(value)) {
        newErrors.Phone = "Phone number must be 10 digits";
      } else {
        delete newErrors.Phone;
      }
    } else if (!value) {
      newErrors[name] = `${name.charAt(0).toUpperCase() +
        name.slice(1)} is required`;
    } else {
      delete newErrors[name];
    }

    setErrors(newErrors);
  };

  // Handle form submission
  const handleSubmit = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);

    console.log("editUser", editUser);
    // eslint-disable-next-line react/prop-types
    console.log("editUser", editUser.ID);

    if (Object.keys(validationErrors).length === 0) {
      const trimmedUserData = {
        Name: editUser.Name,
        Email: editUser.Email,
        Phone: editUser.Phone,
        Address: editUser.Address,
        City: editUser.City,
        State: editUser.State,
        GeoLocation: "",
        Status: editUser.Status,
        // eslint-disable-next-line react/prop-types
        id: editUser.ID,
      };

      try {
        const response = await UpdateUserServer(trimmedUserData);
        if (response.success === 200) {
          notification.success({
            message: "Success",
            description: "User updated successfully.",
          });
          setEditModal(false);
          setHideContent(true);
        } else {
          notification.error({
            message: "Error",
            description: "User update failed.",
          });
        }
      } catch (error) {
        console.error("Update error:", error);
        notification.error({
          message: "Error",
          description: "An error occurred while updating the user.",
        });
      }
    } else {
      notification.error({
        message: "Error",
        description: "Please correct the errors before submitting.",
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="card">
        <div className="head d-flex justify-content-start align-items-center ms-3 mt-5">
          <button
            type="button"
            className="btn btn-link"
            onClick={() => {
              setEditModal(false);
              setHideContent(true);
            }}
          >
            Back
          </button>
          <h5 className="modal-title">Edit User</h5>
        </div>
        <div className="card-body">
          {editUser && (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="Name" className="form-label">
                  Name
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.Name ? "is-invalid" : ""}`}
                  id="Name"
                  name="Name"
                  value={editUser.Name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {errors.Name && (
                  <div className="invalid-feedback">{errors.Name}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="City" className="form-label">
                  City
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.City ? "is-invalid" : ""}`}
                  id="City"
                  name="City"
                  value={editUser.City}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {errors.City && (
                  <div className="invalid-feedback">{errors.City}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="State" className="form-label">
                  State
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.State ? "is-invalid" : ""}`}
                  id="State"
                  name="State"
                  value={editUser.State}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {errors.State && (
                  <div className="invalid-feedback">{errors.State}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="Address" className="form-label">
                  Address
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    errors.Address ? "is-invalid" : ""
                  }`}
                  id="Address"
                  name="Address"
                  value={editUser.Address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {errors.Address && (
                  <div className="invalid-feedback">{errors.Address}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="Phone" className="form-label">
                  Phone
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.Phone ? "is-invalid" : ""}`}
                  id="Phone"
                  name="Phone"
                  value={editUser.Phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  pattern="^[0-9]{10}$"
                  title="Phone number should be 10 digits"
                />
                {errors.Phone && (
                  <div className="invalid-feedback">{errors.Phone}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="Email" className="form-label">
                  Email
                </label>
                <input
                  type="Email"
                  className={`form-control ${errors.Email ? "is-invalid" : ""}`}
                  id="Email"
                  name="Email"
                  value={editUser.Email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {errors.Email && (
                  <div className="invalid-feedback">{errors.Email}</div>
                )}
              </div>
            </form>
          )}
        </div>

        <div className="modal-footer d-flex gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setEditModal(false);
              setHideContent(true);
            }}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
          >
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailEdit;

UserDetailEdit.propTypes = {
  editUser: PropTypes.shape({
    Name: PropTypes.string.isRequired,
    dob: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    Phone: PropTypes.number.isRequired,
    City: PropTypes.string.isRequired,
    Location: PropTypes.string.isRequired,
    State: PropTypes.string.isRequired,
    Address: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    Email: PropTypes.string.isRequired,
    profile: PropTypes.string.isRequired,
    Status: PropTypes.string.isRequired,
  }).isRequired,
  setEditModal: PropTypes.func.isRequired,
  setEditUser: PropTypes.func.isRequired,
  setHideContent: PropTypes.func.isRequired,
};
